// MN-8909: best-effort client-side downscale of oversize claim proof images so
// printers/customers are not blocked by the server's MAX_UPLOAD_BYTES (50 MB)
// rejection (`too_large`). Reuses the canvas raster pipeline from the portal
// photo-upload feature (`resizeImage` — already carries a 100 MP pixel-bomb
// guard and a quality-step compression loop).
//
// Scope: only canvas-decodable raster formats are downscaled. Everything else
// (HEIC/HEIF, AVIF, DNG, TIFF, SVG, GIF, and non-images) is passed through
// untouched so the server-side guard remains the single source of truth for
// them — browsers cannot reliably decode RAW/TIFF, SVG is server-sanitized and
// vector, GIF would be flattened to one frame, and HEIC is highly compressed
// (rarely oversize) and would pull heic2any into the claim bundle.
//
// Any decode/encode failure is caught and returns the original File so the
// request proceeds exactly as it does today. The one residual exposure is a
// pathological extreme-dimension image OOMing the tab during createImageBitmap
// (uncatchable) — identical to the portal pipeline this reuses; real claim
// proof photos are normal-dimension.
import { resizeImage } from "components/jiffy/portal/features/photo-upload/lib/resize-image";

// Canvas-decodable raster formats safe to downscale in-browser.
const DOWNSCALABLE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

// Byte cap mirrors the server's MAX_UPLOAD_BYTES (50.megabytes) exactly — keep
// in sync with packages/core/app/controllers/spree/requests/claims/requests_controller.rb.
// The goal is to unblock uploads, so we keep any resized output the server would
// accept (its check is `size <= MAX_UPLOAD_BYTES`). A tighter cap makes
// resizeImage throw and discard a server-acceptable result, falling back to the
// original oversize file and re-triggering too_large. The real size reduction +
// legibility preservation comes from maxLongEdgePx (deliberately generous vs the
// portal's 2048 px), not the byte cap.
const MAX_OUTPUT_BYTES = 50 * 1024 * 1024;
const MAX_LONG_EDGE_PX = 3500;

export type ClaimDownscaleStatus = "resized" | "bypassed" | "failed";

export type ClaimDownscaleResult = {
  file: File;
  status: ClaimDownscaleStatus;
};

const EXT_BY_MIME: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp"
};

// Realign the filename extension to the encoded MIME. A browser that cannot
// export the requested type (e.g. no image/webp toBlob support) silently falls
// back to image/png; without this the file keeps a .webp name while carrying
// PNG bytes, so its name and content-type disagree.
const nameForMime = (name: string, mime: string): string => {
  const ext = EXT_BY_MIME[mime];
  return ext ? name.replace(/\.[^.]+$/, `.${ext}`) : name;
};

const toFile = (blob: Blob, originalName: string, type: string): File => {
  if (blob instanceof File) return blob;
  return new File([blob], nameForMime(originalName, type), { type });
};

export const downscaleClaimImage = async (file: File): Promise<ClaimDownscaleResult> => {
  if (!DOWNSCALABLE_TYPES.has(file.type)) {
    return { file, status: "bypassed" };
  }

  // Only intervene when the file would actually be rejected as too_large. A
  // file already within the server cap uploads unchanged today, so re-encoding
  // it (canvas re-encode strips EXIF and re-compresses) would mutate evidence
  // the server already accepts — e.g. a 4032×3024 phone JPEG over the long-edge
  // trigger but only a few MB. Bypass it.
  if (file.size <= MAX_OUTPUT_BYTES) {
    return { file, status: "bypassed" };
  }

  try {
    const { blob } = await resizeImage(file, {
      maxLongEdgePx: MAX_LONG_EDGE_PX,
      maxOutputBytes: MAX_OUTPUT_BYTES,
      mimeType: file.type
    });

    // `resizeImage` returns the original File instance when no resize was
    // needed (under both caps); preserve the bypass signal in that case.
    if (blob === file) {
      return { file, status: "bypassed" };
    }

    return { file: toFile(blob, file.name, blob.type || file.type), status: "resized" };
  } catch {
    return { file, status: "failed" };
  }
};
